<script setup lang="ts">
import { ref } from 'vue'
// types
import type { StrapiSiteHeader, StrapiSiteHeaderNavItem } from '@ts-strapi/interfaces'
// hooks
import { useSiteHeader } from '@hooks/widgets/site-header.hook'
// components
import { Logo, Button, ButtonClose } from '@brighterly/lib-lumee-ui'
import { HeaderButton } from '@UI/Headers/HeaderButton'
import { HeaderMenu } from '@UI/Headers/HeaderMenu'
import { HeaderLinkListGroup } from '@UI/Headers/HeaderLinkListGroup'
import { HeaderLinkList } from '@UI/Headers/HeaderLinkList'
import { HeaderLink } from '@UI/Headers/HeaderLink'
import { HeaderBanner } from '@UI/Headers/HeaderBanner'
import { HeaderCtaGroup } from '@UI/Headers/HeaderCtaGroup'

defineOptions({
  name: 'SiteHeader',
})

const props = defineProps<{
  data: StrapiSiteHeader
}>()

const refRoot = ref<HTMLElement | null>(null)

const {
  refOpenedNav,
  refIsSheetOpen,
  refIsGlass,
  refLoginHref,
  refCtaHref,
  refBannerHref,
  resolveUrl,
  toggleNav,
  openNav,
  closeNav,
  scheduleCloseNav,
  cancelCloseNav,
  toggleSheet,
  handleClickLogin,
  handleClickCta,
  handleClickBanner,
  handleClickSeeAll,
  handleNavFocusOut,
} = useSiteHeader({
  data: () => props.data,
  root: refRoot,
})

const isDropdown = (item: StrapiSiteHeaderNavItem): boolean => Boolean(item.groups?.length)
</script>

<template>
  <header
    ref="refRoot"
    class="site-header"
    :class="{ 'site-header--glass': refIsGlass }"
    @mouseleave="scheduleCloseNav"
    @focusout="handleNavFocusOut"
  >
    <div class="site-header__bar">
      <div class="site-header__logo-box">
        <a
          href="/"
          class="site-header__logo"
          aria-label="Brighterly home"
        >
          <Logo
            type="full"
            color="main"
            version="new"
            size="md"
          />
        </a>
      </div>

      <nav
        class="site-header__nav"
        aria-label="Main"
      >
        <HeaderButton
          v-for="(item, index) in data.navItems"
          :key="item.id ?? index"
          :label="item.label"
          :view="isDropdown(item) ? 'dropdown' : 'link'"
          :url="item.url ? resolveUrl(item.url) : ''"
          :open="refOpenedNav === index"
          @mouseenter="isDropdown(item) ? openNav(index) : closeNav()"
          @focusin="isDropdown(item) ? openNav(index) : closeNav()"
          @click="isDropdown(item) && openNav(index)"
        />
      </nav>

      <div class="site-header__actions">
        <HeaderCtaGroup
          view="bar"
          class="site-header__cta"
        >
          <a
            class="site-header__btn-wrap"
            :href="refLoginHref"
            @click="handleClickLogin"
          >
            <Button
              view="secondary"
              size="sm"
              :text="data.login.buttonText"
              tabindex="-1"
            />
          </a>
          <a
            class="site-header__btn-wrap"
            :href="refCtaHref"
            @click="handleClickCta"
          >
            <Button
              view="primary"
              size="sm"
              :text="data.cta.buttonText"
              tabindex="-1"
            />
          </a>
        </HeaderCtaGroup>

        <span class="site-header__burger">
          <ButtonClose
            v-if="refIsSheetOpen"
            view="default"
            size="lg"
            aria-label="Close menu"
            @click="toggleSheet"
          />
          <Button
            v-else
            view="tertiary"
            size="sm"
            icon-only
            trailing-icon="bars"
            aria-label="Open menu"
            @click="toggleSheet"
          />
        </span>
      </div>
    </div>

    <template
      v-for="(item, index) in data.navItems"
      :key="item.id ?? index"
    >
      <nav
        v-if="isDropdown(item)"
        v-show="refOpenedNav === index"
        class="site-header__panel"
        :aria-label="item.label"
        @mouseenter="cancelCloseNav"
      >
        <HeaderMenu :show-banner="Boolean(data.banner)">
          <HeaderLinkListGroup
            v-for="group in item.groups"
            :key="group.id ?? group.title"
            :title="group.title"
          >
            <HeaderLinkList
              v-for="(column, columnIndex) in group.columns"
              :key="column.id ?? columnIndex"
              :subtitle="column.subtitle ?? ''"
              :subtitle-url="column.subtitleUrl ? resolveUrl(column.subtitleUrl) : ''"
            >
              <HeaderLink
                v-for="(link, linkIndex) in column.links"
                :key="link.id ?? linkIndex"
                :label="link.label"
                :url="resolveUrl(link.url)"
                :badge-label="link.badgeLabel ?? ''"
                :disabled="link.disabled"
              />
            </HeaderLinkList>
            <template
              v-if="group.ctaLabel && group.ctaUrl"
              #cta
            >
              <a
                class="site-header__btn-wrap"
                :href="resolveUrl(group.ctaUrl)"
                @click="handleClickSeeAll(group.ctaLabel)"
              >
                <Button
                  view="tertiary-accent"
                  size="sm"
                  trailing-icon="arrow-right"
                  :text="group.ctaLabel"
                  tabindex="-1"
                />
              </a>
            </template>
          </HeaderLinkListGroup>
          <template
            v-if="data.banner"
            #banner
          >
            <HeaderBanner
              :title="data.banner.title"
              :description="data.banner.description ?? ''"
              :button-text="data.banner.buttonText"
              :href="refBannerHref"
              :image="data.banner.image"
              @click="handleClickBanner"
            />
          </template>
        </HeaderMenu>
      </nav>
    </template>

    <nav
      v-if="refIsSheetOpen"
      class="site-header__sheet"
      aria-label="Menu"
    >
      <HeaderMenu :show-banner="false">
        <div
          v-for="(item, index) in data.navItems"
          :key="item.id ?? index"
          class="site-header__sheet-item"
        >
          <HeaderButton
            :label="item.label"
            :view="isDropdown(item) ? 'dropdown' : 'link'"
            :url="isDropdown(item) ? '' : (item.url ? resolveUrl(item.url) : '')"
            :open="refOpenedNav === index"
            @click="isDropdown(item) && toggleNav(index)"
          />
          <div
            v-if="isDropdown(item)"
            v-show="refOpenedNav === index"
            class="site-header__sheet-groups"
          >
            <HeaderLinkListGroup
              v-for="group in item.groups"
              :key="group.id ?? group.title"
              :title="group.title"
            >
              <HeaderLinkList
                v-for="(column, columnIndex) in group.columns"
                :key="column.id ?? columnIndex"
                :subtitle="column.subtitle ?? ''"
                :subtitle-url="column.subtitleUrl ? resolveUrl(column.subtitleUrl) : ''"
              >
                <HeaderLink
                  v-for="(link, linkIndex) in column.links"
                  :key="link.id ?? linkIndex"
                  :label="link.label"
                  :url="resolveUrl(link.url)"
                  :badge-label="link.badgeLabel ?? ''"
                  :disabled="link.disabled"
                />
              </HeaderLinkList>
              <template
                v-if="group.ctaLabel && group.ctaUrl"
                #cta
              >
                <a
                  class="site-header__btn-wrap"
                  :href="resolveUrl(group.ctaUrl)"
                  @click="handleClickSeeAll(group.ctaLabel)"
                >
                  <Button
                    view="tertiary-accent"
                    size="sm"
                    trailing-icon="arrow-right"
                    :text="group.ctaLabel"
                    tabindex="-1"
                  />
                </a>
              </template>
            </HeaderLinkListGroup>
          </div>
        </div>
        <template #cta>
          <HeaderCtaGroup view="sheet">
            <a
              class="site-header__btn-wrap"
              :href="refCtaHref"
              @click="handleClickCta"
            >
              <Button
                view="primary"
                size="md"
                :text="data.cta.buttonText"
                tabindex="-1"
              />
            </a>
            <a
              class="site-header__btn-wrap"
              :href="refLoginHref"
              @click="handleClickLogin"
            >
              <Button
                view="secondary"
                size="md"
                :text="data.login.buttonText"
                tabindex="-1"
              />
            </a>
          </HeaderCtaGroup>
        </template>
      </HeaderMenu>
    </nav>
  </header>
</template>

<style scoped lang="scss" src="@scss/components/Widgets/Headers/SiteHeader.scss" />
